/* Atlas v3 — technical indicator math for the ticker chart.

   Deliberately hand-written rather than pulled from a package. The obvious
   candidate (lightweight-charts-indicators, MIT, 446 indicators) does not
   tree-shake: importing only the six below measured 1,026 kB raw / 267 kB
   gzipped. These same six measure ~1.1 kB raw / 0.6 kB gzipped. See
   DECISION_LOG 2026-08-28.

   Every function takes the normalised bar rows the chart already builds
   ({ time, open, high, low, close, volume }) and returns an array the SAME
   LENGTH as the input, with `null` for bars inside the warm-up window.
   Callers strip nulls before setData() — lightweight-charts wants a
   contiguous series, and a null would render as a gap or throw. */

// Simple moving average of the close.
export function sma(rows, len) {
  const out = new Array(rows.length).fill(null)
  let sum = 0
  for (let i = 0; i < rows.length; i++) {
    sum += rows[i].close
    if (i >= len) sum -= rows[i - len].close
    if (i >= len - 1) out[i] = sum / len
  }
  return out
}

// Exponential moving average, seeded with the SMA of the first `len` closes
// (the conventional seeding — matches TradingView).
export function ema(rows, len) {
  const out = new Array(rows.length).fill(null)
  if (rows.length < len) return out
  const k = 2 / (len + 1)
  let prev = 0
  for (let i = 0; i < len; i++) prev += rows[i].close / len
  out[len - 1] = prev
  for (let i = len; i < rows.length; i++) {
    prev = rows[i].close * k + prev * (1 - k)
    out[i] = prev
  }
  return out
}

// Wilder's RSI (the standard: smoothed averages, not a plain mean).
export function rsi(rows, len = 14) {
  const out = new Array(rows.length).fill(null)
  if (rows.length <= len) return out
  let gain = 0, loss = 0
  for (let i = 1; i <= len; i++) {
    const d = rows[i].close - rows[i - 1].close
    gain += Math.max(d, 0) / len
    loss += Math.max(-d, 0) / len
  }
  // loss can be exactly 0 on an unbroken run up — guard the divide rather
  // than emitting Infinity (which would blow up the pane's price scale).
  out[len] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss)
  for (let i = len + 1; i < rows.length; i++) {
    const d = rows[i].close - rows[i - 1].close
    gain = (gain * (len - 1) + Math.max(d, 0)) / len
    loss = (loss * (len - 1) + Math.max(-d, 0)) / len
    out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss)
  }
  return out
}

// MACD → { macd, signal, hist } per bar. The signal line is an EMA of the
// MACD line, so it is seeded from the first bar where the MACD line exists.
export function macd(rows, fast = 12, slow = 26, sig = 9) {
  const out = new Array(rows.length).fill(null)
  const ef = ema(rows, fast), es = ema(rows, slow)
  const line = rows.map((_, i) => (ef[i] == null || es[i] == null ? null : ef[i] - es[i]))
  const start = line.findIndex((v) => v != null)
  if (start === -1 || line.length - start < sig) return out
  // Re-use ema() by handing it close-shaped rows built from the MACD line.
  const lineRows = line.slice(start).map((v) => ({ close: v }))
  const sigVals = ema(lineRows, sig)
  for (let i = 0; i < sigVals.length; i++) {
    const idx = start + i
    if (sigVals[i] == null) continue
    out[idx] = { macd: line[idx], signal: sigVals[i], hist: line[idx] - sigVals[i] }
  }
  return out
}

// Bollinger Bands → { mid, upper, lower }. Population stddev around the SMA.
export function bollinger(rows, len = 20, mult = 2) {
  const mid = sma(rows, len)
  return rows.map((_, i) => {
    if (mid[i] == null) return null
    let acc = 0
    for (let j = i - len + 1; j <= i; j++) acc += (rows[j].close - mid[i]) ** 2
    const sd = Math.sqrt(acc / len)
    return { mid: mid[i], upper: mid[i] + mult * sd, lower: mid[i] - mult * sd }
  })
}

// VWAP over the loaded window, using the typical price (H+L+C)/3.
// NOTE: a true VWAP resets each session. Our bars are one window (1D intraday
// or N daily bars), so this is a window VWAP — correct and conventional for
// an intraday view, an approximation on multi-day ranges. Labelled in the UI.
export function vwap(rows) {
  let pv = 0, vv = 0
  return rows.map((r) => {
    const tp = (r.high + r.low + r.close) / 3
    pv += tp * (r.volume || 0)
    vv += r.volume || 0
    return vv > 0 ? pv / vv : null
  })
}

// Heikin Ashi: smoothed OHLC. close = avg of the real OHLC, open = running
// average of the previous HA candle. Returns bar rows, not a value series.
export function heikinAshi(rows) {
  const out = []
  let po = null, pc = null
  for (const r of rows) {
    const close = (r.open + r.high + r.low + r.close) / 4
    const open = po == null ? (r.open + r.close) / 2 : (po + pc) / 2
    out.push({
      time: r.time,
      open,
      close,
      high: Math.max(r.high, open, close),
      low: Math.min(r.low, open, close),
      volume: r.volume,
    })
    po = open; pc = close
  }
  return out
}

// Pair a value series with its bar times and drop the warm-up nulls.
export const toLine = (rows, vals) =>
  vals.map((v, i) => (v == null ? null : { time: rows[i].time, value: v })).filter(Boolean)

/* Minimum bars each indicator needs before it produces a single value. Used to
   grey the toggle out rather than let it silently draw nothing — on 1M
   (~22 daily bars) MACD, SMA 50 and SMA 200 all vanished with no explanation,
   which reads as a bug. MACD needs slow(26) + signal(9); RSI needs len + 1. */
export const MIN_BARS = {
  volume: 1, vwap: 1,
  sma20: 20, ema20: 20, bb: 20,
  sma50: 50, ema50: 50,
  sma200: 200,
  rsi: 15,
  macd: 35,
}
